import * as THREE from "three";
import { BaseModify } from "../base/BaseModify";
import { Context } from "../core/context";
import { DBEntity, DbLine, ViewManager } from "@/CadEngine";
import { SelectionManager, Selection_Event } from "./SelectionManager";
import { IndserObject, IndsertPosition } from "../core/IndserObject";
import { PipelineModify, PipelineModifyState } from "@/modify/PipelineModify";
import {
  IModifyOperation,
  ModifyOperateState,
} from "@/modify/IModifyOperation";
import { MirrorModify } from "@/modify/MirrorModify";
import { BreakModify } from "@/modify//BreakModify";
import { ReversalModify, ReversalModifyState } from "@/modify/ReversalModify";
import { ULabel } from "@/ui/ULabel";
import { ExtendModify } from "@/modify/ExtendModify";
import { EditModify } from "@/modify/EditModify";
import { MultiPipeAnnotationModify } from "@/modify/MultiPipeAnnotationModify";
import { SlopeAnnotationModify } from "@/modify/SlopeAnnotationModify";
import { DimensionsAnnotationModify } from "@/modify/DimensionsAnnotationModify";
import {
  AnnotationDrawnModifyAcceptParams,
  DimensionsWireCountModifyAcceptParams,
  DrawCuttingSymbolHandleAcceptParams,
  DrawDoorOrWindowAcceptParams,
  DrawDoorWayLineAcceptParams,
  ExtendHandleParameter,
  InsertConnectPointModifyAcceptParams,
  InsertFileAcceptParams,
  InsertFilePassMatrixAcceptParams,
  LoopAnnotationModifyAcceptParams,
  MultiLineAnnotationModifyAcceptParams,
} from "@/core/Parameters";
import { ClippingModify } from "@/modify/ClippingModify";
import { ChamferModify } from "@/modify/ChamferModify";
import { FilletedCornerModify } from "@/modify/FilletedCornerModify";
import { ElevationAnnotationModify } from "@/modify/ElevationAnnotationModify";
import { DimensionsAnnotationContinuousModify } from "@/modify/DimensionsAnnotationContinuousModify";
import { MultiLineAnnotationModify } from "@/modify/MultiLineAnnotationModify";
import { DrawCuttingSymbolModify } from "@/modify/DrawCuttingSymbolModify";
import { DrawFSCuttingSymbolModify } from "@/modify/DrawFSCuttingSymbolModify";
import { RoteModify } from "@/modify/RoteModify";
import { MoveModify } from "@/modify/MoveModify";
import { AnnotationDrawnModify } from "@/modify/AnnotationDrawnModify";
import { CopyModify } from "@/modify/CopyModify";
import { MeasurementModify } from "@/modify/MeasurementModify";
import { InsertFileModify } from "@/modify/InsertFileModify";
import { InsertFilePassMatrixsModify } from "@/modify/InsertFilePassMatrixsModify";
import { DrawDoorOrWindowModify } from "@/modify/DrawDoorOrWindowModify";
import { DrawOperationMovePointBeforeParams } from "@/draws/IDrawOperation";
import { DrawDoorWayLineModify } from "@/modify/DrawDoorWayLineModify";
import { BatchRoteModify } from "@/modify/BatchRoteModify";
import { LoopAnnotationModify } from "@/modify/LoopAnnotationModify";
import { InsertConnectPointModify } from "@/modify/InsertConnectPointModify";
import { DimensionsWireCountModify } from "@/modify/DimensionsWireCountModify";

class ModifyManager extends BaseModify {
  private m_selectionManager: SelectionManager;
  private onMouseMoveBind: ((e: PointerEvent) => void) | undefined;
  private onMouseUpBind: ((e: PointerEvent) => void) | undefined;
  private onMouseDownBind: ((e: PointerEvent) => void) | undefined;
  private onKeyDownBind: ((e: KeyboardEvent) => void) | undefined;
  private onKeyUpBind: ((e: KeyboardEvent) => void) | undefined;
  private onWheelBind: ((e: WheelEvent) => void) | undefined;
  private onInsertCallbackBind: ((e: IndserObject) => void) | undefined;
  private curpositon:
    | {
        point: THREE.Vector3;
        geo: any;
        position: IndsertPosition;
        obejct: DBEntity[] | THREE.Object3D[];
      }
    | undefined;
  private m_TextLabel: ULabel;
  private m_view: ViewManager;
  private onSnapFilterBind:
    | ((e: DrawOperationMovePointBeforeParams) => void)
    | undefined;
  public get TextLabel() {
    return this.m_TextLabel;
  }
  constructor(context: Context) {
    super(context);
    this.m_selectionManager = this.m_context.SelectionManager;
    this.m_view = context.ViewManager;
    this.m_TextLabel = new ULabel(this.m_view, this.m_view.m_context.Dom);
    this.m_TextLabel.Visibility = false;
  }
  private modifyObject!: IModifyOperation | undefined;
  private m_completeCallback: ((res: any) => void | undefined) | undefined;
  /**
   * move时候触发，未交到直接使用loc，为未矫正的坐标
   * @param insert
   */
  public onInsertCallback(insert: IndserObject) {
    let p = insert.adjust ?? insert.location;
    this.curpositon = {
      point: new THREE.Vector3(p.x, p.y, p.z),
      geo: insert.geo,
      position: insert.position,
      obejct: insert.obejct,
    };
    this.modifyObject?.OnMovePoint(this.curpositon);
  }

  private ModifyEnd() {
    this.m_context.SelectionManager.ClearSelected();
    this.m_selectionManager.HighLighting = true;
    this.m_context.ViewManager.Controls.MouseLeftPan = true;
    this.m_context.SelectionManager.CancelPick();
    this.ModifyUnInit();
    this.m_context.ViewManager.Render();
    this.m_TextLabel.Visibility = false;
  }

  public ModifyFinish(operate: ModifyOperateState) {
    let dbentity = this.modifyObject?.Finish(operate);
    if (dbentity && this.m_completeCallback) {
      this.m_completeCallback(dbentity as DBEntity);
    }
    this.ModifyEnd();
    this.m_completeCallback = undefined;
    this.modifyObject = undefined;
  }

  private initEvent() {
    this.onMouseMoveBind = this.onMouseMove.bind(this);
    this.m_context.ViewManager.EventManager.addEventListener(
      "pointermove",
      this.onMouseMoveBind
    );
    this.onMouseUpBind = this.onMouseUp.bind(this);
    this.m_context.ViewManager.EventManager.addEventListener(
      "pointerup",
      this.onMouseUpBind
    );
    this.onMouseDownBind = this.onMouseDown.bind(this);
    this.m_context.ViewManager.EventManager.addEventListener(
      "pointerdown",
      this.onMouseDownBind
    );
    this.onKeyDownBind = this.onKeyDown.bind(this);
    window.addEventListener("keydown", this.onKeyDownBind);

    this.onKeyUpBind = this.onKeyUp.bind(this);
    window.addEventListener("keyup", this.onKeyUpBind);

    this.onWheelBind = this.onWheel?.bind(this);
    window.addEventListener("wheel", this.onWheelBind);

    this.onInsertCallbackBind = this.onInsertCallback.bind(this);
    this.m_selectionManager.EventManager.addEventListener(
      Selection_Event.inserts,
      this.onInsertCallbackBind
    );

    this.onSnapFilterBind = this.onSnapFilter.bind(this);
    this.m_selectionManager.EventManager.addEventListener(
      Selection_Event.snapFilter,
      this.onSnapFilterBind
    );
  }

  private unIntEvent() {
    if (this.onMouseMoveBind)
      this.m_context.ViewManager.EventManager.removeEventListener(
        "pointermove",
        this.onMouseMoveBind
      );
    if (this.onMouseUpBind)
      this.m_context.ViewManager.EventManager.removeEventListener(
        "pointerup",
        this.onMouseUpBind
      );
    if (this.onMouseDownBind)
      this.m_context.ViewManager.EventManager.removeEventListener(
        "pointerdown",
        this.onMouseDownBind
      );
    if (this.onKeyDownBind)
      window.removeEventListener("keydown", this.onKeyDownBind);

    if (this.onKeyUpBind) window.removeEventListener("keyup", this.onKeyUpBind);

    if (this.onWheelBind) {
      window.removeEventListener("wheel", this.onWheelBind);
    }
    if (this.onInsertCallbackBind) {
      this.m_selectionManager.EventManager.removeEventListener(
        Selection_Event.inserts,
        this.onInsertCallbackBind
      );
    }
  }
  private onSnapFilter(param: DrawOperationMovePointBeforeParams) {
    // if(this.modifyObject instanceof DrawWallModify){
    //   this.modifyObject?.OnMovePointBefore(param);
    // }
  }

  private onMouseMove(e: PointerEvent): void {
    this.modifyObject?.onMouseMove(e);
  }

  private onMouseUp(e: PointerEvent): void {
    this.modifyObject?.onMouseUp(e);
  }

  private onMouseDown(e: PointerEvent): void {
    this.modifyObject?.onMouseDown(e);
    if (this.curpositon && e.button == 0) {
      this.modifyObject?.OnDownPoint(this.curpositon);
    }
  }

  private onKeyDown(e: KeyboardEvent) {
    this.modifyObject?.onKeyDown(e);
    if (e.key == "Escape") {
      if (this.modifyObject instanceof MirrorModify) {
        this.ModifyFinish(ModifyOperateState.cancel);
      } else if (this.modifyObject instanceof BreakModify) {
        this.ModifyFinish(ModifyOperateState.cancel);
      } else if (this.modifyObject instanceof PipelineModify) {
        this.ModifyFinish(ModifyOperateState.cancel);
      } else if (this.modifyObject instanceof DrawCuttingSymbolModify) {
        this.ModifyFinish(ModifyOperateState.cancel);
      } else if (this.modifyObject instanceof DrawFSCuttingSymbolModify) {
        this.ModifyFinish(ModifyOperateState.cancel);
      } else if (this.modifyObject instanceof RoteModify) {
        this.ModifyFinish(ModifyOperateState.cancel);
      } else if (this.modifyObject instanceof MoveModify) {
        this.ModifyFinish(ModifyOperateState.cancel);
      } else if (this.modifyObject instanceof CopyModify) {
        this.ModifyFinish(ModifyOperateState.cancel);
      } else if (this.modifyObject instanceof MirrorModify) {
        this.ModifyFinish(ModifyOperateState.cancel);
      }
    }
  }
  private onKeyUp(e: KeyboardEvent) {
    this.modifyObject?.onKeyUp(e);
  }
  private onWheel(e: WheelEvent) {
    this.modifyObject?.OnWheel(e);
  }
  // public isEnableSnap:boolean=true;

  private ModifyInit() {
    this.m_selectionManager.EnableSnap(true);
    this.m_context.Dom.style.cursor = "none";
    this.initEvent();
  }
  private ModifyUnInit() {
    this.m_selectionManager.EnableSnap(false);
    this.m_context.Dom.style.cursor = "default";
    this.unIntEvent();
    this.m_view.Render();
  }

  // 管线的操作(置上或置下)
  public PipelineHandle(
    type: PipelineModifyState,
    callBack: (result: any) => void
  ) {
    this.modifyObject = new PipelineModify(this.m_context, type);
    this.m_completeCallback = callBack;
    this.ModifyInit();
  }

  // 镜像
  public MirrorHandle(callBack: (result: any) => void) {
    this.modifyObject = new MirrorModify(this.m_context);
    this.m_completeCallback = callBack;
    this.ModifyInit();
  }
  // 打断/断点
  public BreakHandle(
    callBack: (result: any) => void,
    callbackUnderway: (point: THREE.Vector3) => void,
    accordLayerNames: string[]
  ) {
    this.modifyObject = new BreakModify(
      this.m_context,
      callbackUnderway,
      accordLayerNames
    );
    this.m_completeCallback = callBack;
    this.ModifyInit();
  }
  // 反转
  public ReversalHandle(
    callBack: (result: any) => void,
    type: ReversalModifyState
  ) {
    this.modifyObject = new ReversalModify(this.m_context, type);
    this.m_completeCallback = callBack;
  }
  // 延申
  public ExtendHandle(
    handleData: ExtendHandleParameter,
    callBack: (result: any) => void
  ) {
    this.modifyObject = new ExtendModify(this.m_context, handleData);
    this.m_completeCallback = callBack;
  }

  // 编辑
  public EditHandle(callBack: (result: DbLine[]) => void) {
    this.modifyObject = new EditModify(this.m_context);
    this.m_completeCallback = callBack;
    this.ModifyInit();
  }

  // 多管标注
  public MultiPipeAnnotationHandle(
    callBack: (result: any) => void,
    callbackUnderway: (point: THREE.Vector3) => void
  ) {
    this.modifyObject = new MultiPipeAnnotationModify(
      this.m_context,
      callbackUnderway
    );
    this.m_completeCallback = callBack;
    this.ModifyInit();
  }

  // 坡度标注
  public SlopeAnnotationHandle(
    callBack: (result: any) => void,
    callbackUnderway: (point: THREE.Vector3) => void
  ) {
    this.modifyObject = new SlopeAnnotationModify(
      this.m_context,
      callbackUnderway
    );
    this.m_completeCallback = callBack;
    this.ModifyInit();
  }

  // 尺寸标注
  public DimensionsAnnotationHandle(callBack: (result: any) => void) {
    this.modifyObject = new DimensionsAnnotationModify(this.m_context);
    this.m_completeCallback = callBack;
    this.ModifyInit();
  }
  // 尺寸标注连续
  public DimensionsAnnotationContinuousHandle(callBack: (result: any) => void) {
    this.modifyObject = new DimensionsAnnotationContinuousModify(
      this.m_context
    );
    this.m_completeCallback = callBack;
    this.ModifyInit();
  }

  // 标高标注
  public ElevationAnnotationHandle(
    callBack: (result: any) => void,
    callbackUnderway: (point: THREE.Vector3) => void
  ) {
    this.modifyObject = new ElevationAnnotationModify(
      this.m_context,
      callbackUnderway
    );
    this.m_completeCallback = callBack;
    this.ModifyInit();
  }
  // 多行标注
  public MultiLineAnnotationHandle(
    callBack: (result: any) => void,
    underwayCallback: (points: THREE.Vector3[]) => void,
    acceptParams: MultiLineAnnotationModifyAcceptParams
  ) {
    this.modifyObject = new MultiLineAnnotationModify(
      this.m_context,
      underwayCallback,
      acceptParams
    );
    this.m_completeCallback = callBack;
    this.ModifyInit();
  }

  //剪切
  public ClippingHandle(callBack: (result: any) => void) {
    this.modifyObject = new ClippingModify(this.m_context);
    this.m_completeCallback = callBack;
    this.ModifyInit();
  }

  //倒角
  public ChamferHandle(
    callBack: (result: any) => void,
    chamferLength: number,
    accordLayerNames: string[]
  ) {
    this.modifyObject = new ChamferModify(
      this.m_context,
      chamferLength,
      accordLayerNames
    );
    this.m_completeCallback = callBack;
    this.ModifyInit();
  }

  //圆角
  public FilletedCornerHandle(
    callBack: (result: any) => void,
    chamferLength: number,
    accordLayerNames: string[]
  ) {
    this.modifyObject = new FilletedCornerModify(
      this.m_context,
      chamferLength,
      accordLayerNames
    );
    this.m_completeCallback = callBack;
    this.ModifyInit();
  }

  // 剖切符
  public DrawCuttingSymbolHandle(params: DrawCuttingSymbolHandleAcceptParams) {
    this.modifyObject = new DrawCuttingSymbolModify(this.m_context, params);
    this.m_completeCallback = params.callBackFinish;
    this.ModifyInit();
  }

  // 断面剖切符
  public DrawFSCuttingSymbolHandle(
    params: DrawCuttingSymbolHandleAcceptParams
  ) {
    this.modifyObject = new DrawFSCuttingSymbolModify(this.m_context, params);
    this.m_completeCallback = params.callBackFinish;
    this.ModifyInit();
  }
  // 标注绘制
  public AnnotationDrawHandle(params: AnnotationDrawnModifyAcceptParams) {
    this.modifyObject = new AnnotationDrawnModify(this.m_context, params);
    this.m_completeCallback = params.callBackFinish;
    this.ModifyInit();
  }

  //旋转
  public RotateHandle(callBack: (result: any) => void) {
    this.modifyObject = new RoteModify(this.m_context);
    this.m_completeCallback = callBack;
    this.ModifyInit();
  }
  //移动
  public MoveHandle(callBack: (result: any) => void) {
    this.modifyObject = new MoveModify(this.m_context);
    this.m_completeCallback = callBack;
    this.ModifyInit();
  }

  //复制
  public CopyHandle(callBack: (result: any) => void) {
    this.modifyObject = new CopyModify(this.m_context);
    this.m_completeCallback = callBack;
    this.ModifyInit();
  }

  // 测量
  public MeasurementHandle(callBack: (result: any) => void) {
    this.modifyObject = new MeasurementModify(this.m_context);
    this.m_completeCallback = callBack;
    this.ModifyInit();
  }

  // 插入
  public InsertFileHandle(params: InsertFileAcceptParams) {
    this.modifyObject = new InsertFileModify(this.m_context, params);
    // this.m_completeCallback = params.callBack;
    this.ModifyInit();
  }

  // 通过矩阵插入文件
  public InsertFilePassMatrixsHandle(params: InsertFilePassMatrixAcceptParams) {
    this.modifyObject = new InsertFilePassMatrixsModify(this.m_context, params);
    this.m_completeCallback = params.callBackFinish;
    this.ModifyInit();
  }

  // 绘制门窗
  public DrawDoorOrWindowHandle(params: DrawDoorOrWindowAcceptParams) {
    this.modifyObject = new DrawDoorOrWindowModify(this.m_context, params);
    this.m_completeCallback = params.callBackFinish;
    this.ModifyInit();
  }
  // 绘制门口线
  public DrawDoorWayLineHandle(params: DrawDoorWayLineAcceptParams) {
    this.modifyObject = new DrawDoorWayLineModify(this.m_context, params);
    this.m_completeCallback = params.callBackFinish;
    this.ModifyInit();
  }
  //批量旋转
  public BatchRotateHandle(rotate: number) {
    this.modifyObject = new BatchRoteModify(this.m_context, rotate);
    // this.m_completeCallback = callBack;
    // this.ModifyInit();
  }

  // 回路标注
  public LoopAnnotationHandle(params: LoopAnnotationModifyAcceptParams) {
    this.modifyObject = new LoopAnnotationModify(this.m_context, params);
    this.m_completeCallback = params.callBackFinish;
    this.ModifyInit();
  }
  // 插入连接点
  public InsertConnectPointHandle(
    params: InsertConnectPointModifyAcceptParams
  ) {
    this.modifyObject = new InsertConnectPointModify(this.m_context, params);
    this.m_completeCallback = params.callBackFinish;
    this.ModifyInit();
  }

  // 标导线数
  public DimensionsWireCountHandle(
    params: DimensionsWireCountModifyAcceptParams
  ) {
    this.modifyObject = new DimensionsWireCountModify(this.m_context, params);
    this.m_completeCallback = params.callBackFinish;
    this.ModifyInit();
  }
}

export { ModifyManager };
